import type { HighFrequency_ResultsAndWinnersDraw } from '../../../../../core/api/shared/@types/generated/backend/graphql-schema-types'

import { useHighFrequencyResultsAndWinnersUpdatesSubscription } from '../../../../../core/api/app/composables/subscription.generated'
import { HFG_DRAW_RESULT_MAIN_FIELD } from '../components/highFrequencyGameConstants'
import { isHighFrequencyMultiplierAddOn } from '../components/highFrequencyGameGuards'

type BonusValue = number | string

export type KenoDrawSlide = {
  drawNumber: string
  drawnNumbers: string[]
  bonus?: BonusValue
}

function extractDrawnNumbers(draw: HighFrequency_ResultsAndWinnersDraw): string[] {
  const mainResult = draw.results.find((result) => result.name === HFG_DRAW_RESULT_MAIN_FIELD)
  return mainResult?.values.map((value) => String(value.value)) ?? []
}

function extractBonus(draw: HighFrequency_ResultsAndWinnersDraw): BonusValue | undefined {
  const bonusResult = draw.results.find((result) => isHighFrequencyMultiplierAddOn(result.name))
  return bonusResult?.values[0]?.value
}

function toSlide(draw: HighFrequency_ResultsAndWinnersDraw): KenoDrawSlide {
  return {
    bonus: extractBonus(draw),
    drawNumber: draw.drawNumber.toString(),
    drawnNumbers: extractDrawnNumbers(draw),
  }
}

export function useKenoHub() {
  const gameId = useHighFrequencyGameId()
  const licensedTerritory = useLicensedTerritory({ raw: true })
  const { navigateToLocalized } = useRouterUtils()
  const { log } = useDatadog()

  const pinnedDrawNumber = useState<Maybe<number>>(() => null)
  const isGameNotFound = useState(() => false)
  const searchDrawNumber = useState<Maybe<number>>(() => null)
  const winnersCount = useState<Maybe<number>>(() => null)
  const newDrawNumber = ref<Maybe<number>>(null)
  const minLoadingActive = ref(false)
  const { start: startMinLoading } = useTimeoutFn(() => (minLoadingActive.value = false), 500, {
    immediate: false,
  })

  const { onResult: onNewResults } = useHighFrequencyResultsAndWinnersUpdatesSubscription({
    licensedTerritory,
  })

  const { result, onResult, fetchMore, loading } = useHighFrequencyResultsAndWinnersQuery({
    input: {
      gameId,
      licensedTerritory,
      limit: 3,
    },
  })

  const {
    result: searchResult,
    loading: searchLoading,
    onResult: onSearchResult,
    onError: onSearchError,
  } = useHighFrequencyResultsAndWinnersQuery(
    () => ({
      input: {
        gameId,
        licensedTerritory,
        drawNumber: searchDrawNumber.value,
        limit: 1,
      },
    }),
    () => ({
      enabled: searchDrawNumber.value !== null,
    }),
  )

  const isSearching = computed(() => searchLoading.value || minLoadingActive.value)

  watch(searchLoading, (isLoading) => {
    if (isLoading) {
      minLoadingActive.value = true
      startMinLoading()
    }
  })

  const currentIndex = computed(() => {
    if (pinnedDrawNumber.value === null) return 0
    const index = draws.value.findIndex((draw) => draw.drawNumber === pinnedDrawNumber.value)
    return index === -1 ? 0 : index
  })

  const canNavigateOlder = computed(
    () => !isInSearchMode.value && currentIndex.value < draws.value.length - 1,
  )

  const canNavigateNewer = computed(() => !isInSearchMode.value && currentIndex.value > 0)

  const currentDraw = computed(() => {
    if (isInSearchMode.value) {
      return searchResultData.value?.draws[0] ?? null
    }
    return draws.value[currentIndex.value] ?? null
  })

  const currentDrawNumber = computed<string | undefined>(() => {
    const drawNumber = currentDraw.value?.drawNumber
    if (!drawNumber) return undefined
    return drawNumber.toString()
  })

  const slideDraws = computed<KenoDrawSlide[]>(() => {
    if (isInSearchMode.value) {
      const searchDraw = searchResultData.value?.draws[0]
      return searchDraw ? [toSlide(searchDraw)] : []
    }
    return draws.value.map(toSlide)
  })

  const draws = computed(() => resultData.value?.draws ?? [])

  const gameConfig = computed(() => resultData.value?.gameConfig)

  const hasNewGame = computed(() => newDrawNumber.value !== null && !isLatest.value)

  const isInSearchMode = computed(() => searchDrawNumber.value !== null)

  const isLatest = computed(
    () =>
      (!isInSearchMode.value && pinnedDrawNumber.value === null) ||
      (searchDrawNumber.value === latestDrawNumber.value && !searchLoading.value),
  )

  const latestDrawNumber = computed(() => {
    const latestDraw = draws.value[0]
    return latestDraw?.drawNumber ?? null
  })

  const resultData = computed(() => {
    const data = result.value?.highFrequencyResultsAndWinners
    return isGraphqlSuccess(data) ? data : null
  })

  const searchResultData = computed(() => {
    if (searchDrawNumber.value === null) return null
    const data = searchResult.value?.highFrequencyResultsAndWinners
    return isGraphqlSuccess(data) ? data : null
  })

  const handleNewGame = () => {
    newDrawNumber.value = null
    pinnedDrawNumber.value = null
    searchDrawNumber.value = null
    isGameNotFound.value = false
  }

  const handleChange = (drawNumber: string) => {
    const parsed = parseInt(drawNumber, 10)
    if (isNaN(parsed)) return

    if (parsed === latestDrawNumber.value) {
      pinnedDrawNumber.value = null
      newDrawNumber.value = null
      return
    }

    pinnedDrawNumber.value = parsed
  }

  const handlePlayNow = () => {
    if (!gameId) return
    return navigateToLocalized(`/keno/${gameId.toLowerCase()}`)
  }

  const resetSearch = () => {
    searchDrawNumber.value = null
    isGameNotFound.value = false
    pinnedDrawNumber.value = null
  }

  const handleSearch = (gameNumber?: Maybe<string>) => {
    if (!gameNumber) return resetSearch()

    const parsed = parseInt(gameNumber, 10)
    if (isNaN(parsed)) return

    if (searchDrawNumber.value === parsed) return
    isGameNotFound.value = false
    minLoadingActive.value = true
    startMinLoading()
    searchDrawNumber.value = parsed
  }

  onResult((result) => {
    if (winnersCount.value !== null) return

    const data = result.data?.highFrequencyResultsAndWinners
    if (!data || isGraphqlFailure(data)) return

    winnersCount.value = data.winnersCount
  })

  onSearchResult((result) => {
    const data = result.data?.highFrequencyResultsAndWinners
    if (!data || (isGraphqlSuccess(data) && data.draws.length > 0)) return
    isGameNotFound.value = true
  })

  onSearchError(() => (isGameNotFound.value = true))

  onNewResults(async ({ data }) => {
    const drawNumber = data?.highFrequencyResultsAndWinnersUpdates.drawNumber
    if (!drawNumber || newDrawNumber.value === drawNumber || drawNumber === latestDrawNumber.value)
      return

    newDrawNumber.value = drawNumber

    if (pinnedDrawNumber.value === null) {
      pinnedDrawNumber.value = latestDrawNumber.value
    }

    try {
      await fetchMore({
        variables: {
          input: {
            gameId,
            licensedTerritory,
            drawNumber,
            limit: 1,
          },
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev

          const prevData = prev.highFrequencyResultsAndWinners
          const newData = fetchMoreResult.highFrequencyResultsAndWinners

          if (!isGraphqlSuccess(prevData) || !isGraphqlSuccess(newData)) return prev

          return {
            highFrequencyResultsAndWinners: {
              ...prevData,
              draws: [...newData.draws, ...prevData.draws],
              winnersCount: newData.winnersCount,
            },
          }
        },
      })
    } catch (error) {
      log('error', {
        component: 'kenoHub.ts',
        error,
        message: `Failed to fetch new results for game ${gameId}`,
      })
    }
  })

  return {
    canNavigateNewer,
    canNavigateOlder,
    currentDrawNumber,
    draws: slideDraws,
    gameConfig,
    handleChange,
    handleNewGame,
    handlePlayNow,
    handleSearch,
    hasNewGame,
    isGameNotFound,
    isLatest,
    isSearching,
    latestDrawNumber,
    loading,
    newDrawNumber: readonly(newDrawNumber),
    winnersCount,
  }
}
